import { useQuery } from '@tanstack/react-query';
import Marketplace from '../services/marketplace';
import { Apps } from '../services/apps';

const TWO_MIN = 2 * 60 * 1000;

// Query key for the managed-apps catalog. A submitted rating patches THIS cache
// (queryClient.setQueryData) so the list tile updates instantly without a
// refetch — keep the key in sync with AppsMarketplaceSection's setQueryData.
export const APPS_CATALOG_KEY = ['apps', 'catalog'];

/**
 * The marketplace "trending" catalog (list `items` + ordered `featured` hero
 * rows), React-Query cached.
 *
 * Why this is the SAFEST thing to cache: the catalog is READ-ONLY — users never
 * mutate it from the client — so there is nothing to invalidate against and no
 * sync issue is possible. The only freshness concern is the backend adding a new
 * agent, which is rare; a stale window handles it.
 *
 * Cache strategy (conservative):
 *   - staleTime 2min → within-session navigation to /marketplace serves the
 *     cache instantly (no refetch, no loading flash). The catalog changes
 *     rarely, so up to 2-min-stale is fine.
 *   - The app's persisted cache (PersistQueryClientProvider) paints this from
 *     disk on a hard refresh too, then revalidates on mount once the 2-min
 *     window has lapsed.
 *
 * Returns the raw query — callers derive `items` / `featured` from `data` and
 * map `isLoading`/`error`/`refetch` (the old manual fetch's loading/error/retry).
 */
export const useMarketplace = (limit = 24, options = {}) => {
  return useQuery({
    queryKey: ['marketplace', 'trending', limit],
    queryFn: () => Marketplace.listTrending(limit),
    staleTime: TWO_MIN,
    ...options,
  });
};

/**
 * The managed-apps catalog (the Apps marketplace tiles), React-Query cached.
 *
 * Same rationale + strategy as useMarketplace: a read-only catalog, cached
 * within-session (staleTime 2min) so navigating back is instant + flash-free.
 * The ONE local mutation — patching a tile's rating after the user rates — is
 * applied to THIS cache via queryClient.setQueryData(APPS_CATALOG_KEY, …) in
 * the component, so the rating chip updates immediately without a refetch.
 *
 * Returns the raw catalog (`data.items`); the component filters out
 * `disabled` tiles (the deploy path 4xx's them anyway).
 */
export const useAppsCatalog = (options = {}) => {
  return useQuery({
    queryKey: APPS_CATALOG_KEY,
    queryFn: () => Apps.listCatalog(),
    staleTime: TWO_MIN,
    ...options,
  });
};
